import { InvitationStore } from "../store/invitation";
import { LoginStore } from "../store/login";
import { OnboardingStore } from "../store/onboarding";
import { OrgSettingsAPI } from "src/api";
import { apiFetch } from "src/api/fetch";
import { BrandingStore, OrgSettingsStore, OrgStore, UserStore } from "src/store";

export function login(data = {}) {
  return async (dispatch) => {
    let json;
    dispatch(LoginStore.actions.setLoading());
    const options = {
      method: "POST",
      body: JSON.stringify(data),
    };

    const response = await apiFetch("/api/v1/sessions", options);

    try {
      json = await response.json();
    } catch (e) {
      return dispatch(
        LoginStore.actions.setError("Sorry, something went wrong. Please try again."),
      );
    }

    if (response.ok) {
      dispatch(OrgStore.actions.upsertOne(json.org));
      dispatch(BrandingStore.actions.upsertOne(json.branding));
      dispatch(OrgSettingsStore.actions.upsertOne(json.org_settings));
      dispatch(fetchUser());
      return true;
    }

    if (response.status === 429) {
      return dispatch(
        LoginStore.actions.setError("Too many sign in attempts. Please try again later."),
      );
    }

    if (response.status === 422) {
      return dispatch(
        LoginStore.actions.setError("Access to this organization is not allowed at this time."),
      );
    }

    if (json.error) {
      return dispatch(
        LoginStore.actions.setError(
          "This email and password combination is incorrect. Please check both and try again.",
        ),
      );
    }
    if (json.orgs) {
      dispatch(OrgStore.actions.upsertMany(json.orgs));
      return dispatch(
        LoginStore.actions.update({
          email: json.email,
          tokenUUID: json.token_uuid,
        }),
      );
    }

    dispatch(LoginStore.actions.setError("Sorry, something went wrong. Please try again."));
  };
}

export function fetchUser() {
  return async (dispatch) => {
    const response = await apiFetch("/api/v1/users/self");

    if (response.ok) {
      const user = await response.json();
      dispatch(UserStore.actions.upsertOne(user));
      dispatch(
        LoginStore.actions.update({
          firstName: user.first_name,
          userId: user.id,
          email: user.email,
        }),
      );
      dispatch(OrgSettingsAPI.fetchOrgSettings());
      return true;
    }

    dispatch(LoginStore.actions.setError("Failed to fetch user data"));
  };
}

export function updateUser(user) {
  return async () => {
    const response = await apiFetch("/api/v1/users/self", {
      body: JSON.stringify(user),
      method: "PUT",
    });

    if (response.ok) {
      const updatedUser = await response.json();
      return { data: updatedUser };
    }

    return { error: "Failed to update user." };
  };
}

export function fetchOnboarding() {
  return async (dispatch) => {
    dispatch(OnboardingStore.actions.setLoading(true));
    const response = await apiFetch("/api/v1/onboarding");

    if (response.ok) {
      const onboarding = await response.json();
      dispatch(OnboardingStore.actions.set(onboarding));
      return true;
    }

    dispatch(OnboardingStore.actions.setError(new Error("Failed to fetch onboarding")));
  };
}

export function updateOnboarding(data) {
  return async (dispatch) => {
    const response = await apiFetch("/api/v1/onboarding", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const onboarding = await response.json();
      return { data: onboarding };
    }

    dispatch(OnboardingStore.actions.setError(new Error("Failed to fetch onboarding")));
  };
}

export function validateInvitation(invitationId, orgId) {
  return async (dispatch) => {
    dispatch(InvitationStore.actions.setLoading());
    const response = await apiFetch(
      `/api/v1/invitations/verify?invitation_uuid=${invitationId}&org_id=${orgId}`,
    );

    if (response.ok) {
      dispatch(InvitationStore.actions.setIsValid(true));
      return true;
    }
    const text = JSON.parse(await response.text());
    const errorMessage = text.error || "Invalid invitation.";
    dispatch(InvitationStore.actions.setIsValid(false));
    dispatch(LoginStore.actions.setError(errorMessage));
    return false;
  };
}

export async function validateAuthToken() {
  const response = await apiFetch("/api/v1/sessions");
  if (!response.ok) {
    throw new Error("Invalid auth token");
  }
}

export async function acceptTermsOfService() {
  const options = {
    method: "POST",
  };
  const response = await apiFetch("/api/v1/users/accept_terms_of_service", options);
  if (response.ok) {
    return true;
  }

  throw new Error("Failed to accept Terms of Service.");
}

export function redeemInvitation(invitationId, orgId, password) {
  return async (dispatch) => {
    dispatch(InvitationStore.actions.setLoading());
    dispatch(LoginStore.actions.setLoading());
    const options = {
      method: "POST",
      body: JSON.stringify({
        invitation_uuid: invitationId,
        org_id: orgId,
        password,
      }),
    };
    const response = await apiFetch("/api/v1/set_password", options);

    try {
      await response.json();
    } catch (e) {
      dispatch(InvitationStore.actions.unsetLoading());
      return dispatch(
        LoginStore.actions.setError("Sorry, something went wrong. Please try again."),
      );
    }

    if (response.ok) {
      dispatch(InvitationStore.actions.setIsRedeemed(true));
      dispatch(fetchUser());
    }
  };
}

export function requestPasswordChange(data) {
  return async (dispatch) => {
    const response = await apiFetch("/api/v1/pwd/request_password_change", {
      method: "POST",
      body: JSON.stringify(data),
    });
    let json;
    try {
      json = await response.json();
    } catch (e) {
      console.error(e);
      dispatch(LoginStore.actions.setError("Sorry, something went wrong. Please try again."));
      return;
    }

    if (response.ok) {
      const { message } = json;
      dispatch(LoginStore.actions.update({ contextualMessage: message }));
      return;
    }
    throw new Error("Failed to reset password.");
  };
}

export async function resetPassword(data) {
  const response = await apiFetch("/api/v1/pwd/reset_password", {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (response.ok) {
    const json = await response.json();
    return json;
  }
  throw new Error("Failed to reset password.");
}

export function forgotOrg(data) {
  return async (dispatch) => {
    dispatch(LoginStore.actions.setLoading());
    const response = await apiFetch("/api/v1/forgot-org", {
      method: "POST",
      body: JSON.stringify(data),
    });
    let json;
    try {
      json = await response.json();
    } catch (e) {
      console.error(e);
      dispatch(LoginStore.actions.setError("Sorry, something went wrong. Please try again."));
      return;
    }

    if (response.ok) {
      const changes = {};
      if (json.user_id) changes.userId = json.user_id;
      if (json.first_name) changes.firstName = json.first_name;
      if (json.email) changes.email = json.email;
      if (json.message) changes.contextualMessage = json.message;
      dispatch(LoginStore.actions.update(changes));
      return;
    }
    throw new Error("Failed to send link.");
  };
}

export async function fetchActiveCookies() {
  const response = await apiFetch("/get_cookies");

  if (response.ok) {
    // no content
    if (response.status === 204) return [];
    const json = await response.json();
    return json;
  }
  throw new Error("Failed fetch active cookies");
}
